import React, { useState } from 'react';
import { HelpOutline, Close } from '@material-ui/icons';
import { Formik } from 'formik';
import {
    TextField,
    withStyles,
    Select,
    MenuItem,
    FormControl,
    Button,
    InputLabel,
    Tooltip,
    IconButton,
    FormControlLabel,
    FormGroup,
    Checkbox,
    CircularProgress
} from '@material-ui/core';
import * as Yup from 'yup';
import JsonMappingHelp from './JsonMappingHelp';
import FormStep2 from './FormStep2';

const styles = theme => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2)
    },
    button: {
        width: '120px',
        marginTop: '10px',
        marginRight: '10px'
    },
    formControl: {
        width: '100%'
    }
});

const FormStep1Kloudless = props => {
    const {
        classes,
        formSettings,
        initialValues,
        translations,
        handleSubmit, 
        isDirty,
        edit
    } = props;

    const [mappingHelp, setMappingHelp] = useState(false);
    const handleMappingHelp = () => {
        setMappingHelp(!mappingHelp);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={Yup.object().shape(formSettings.Validation)}
            onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);
                await handleSubmit(values);
            }}
            validateOnMount={true}
        >
            {formikProps => {
                const { values, dirty, touched, errors, isValid, isSubmitting, handleChange, handleBlur, handleSubmit } = formikProps;

                return (
                    <div className={classes.root}>
                        <form onSubmit={handleSubmit}>
                            <>
                                <TextField
                                    name="Name"
                                    value={values.Name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.Name && touched.Name)}
                                    placeholder={translations["name"] || "Name"}
                                    label={translations["name"] || "Name"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                                <FormControl id="direction_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations['please_select_direction'] || "Please select direction"}</InputLabel>
                                    <Select
                                        name="Direction"
                                        label={translations['direction'] || 'Direction'}
                                        margin={"dense"}
                                        value={values.Direction}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value={"in"}>
                                            {translations['in'] || 'in'}
                                        </MenuItem>
                                        <MenuItem value={"out"}>
                                            {translations['out'] || 'out'}
                                        </MenuItem>
                                        <MenuItem value={"in+out"}>
                                            {`${translations['in'] || 'in'}+${translations['out'] || 'out'}`}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl id="active_select" className={classes.formControl} margin={"dense"}>
                                    <InputLabel shrink>{translations["active"] || "Active"}</InputLabel>
                                    <Select
                                        name="Active"
                                        value={values.Active}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={!!(errors.Active && touched.Active)}
                                        placeholder={translations["active"] || "Active"}
                                        label={translations["active"] || "Active"}
                                        margin={"dense"}
                                        fullWidth={true}
                                    >
                                        <MenuItem value={true}>
                                            {translations['active'] || 'Active'}
                                        </MenuItem>
                                        <MenuItem value={false}>
                                            {translations['inactive'] || 'Inactive'}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    name="BearerToken"
                                    value={values.BearerToken}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={!!(errors.BearerToken && touched.BearerToken)}
                                    placeholder={translations['bearer_token'] || 'Bearer Token'}
                                    label={translations['bearer_token'] || 'Bearer Token'}
                                    margin={'dense'}
                                    fullWidth={true}
                                />
                                <InputLabel shrink>{translations['select_external_triggers'] || "Please select triggers for external service"}</InputLabel>
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnSessionStart}
                                                onChange={handleChange}
                                                name="TriggerOnSessionStart"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_start_session'] || 'On start session'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnModifiedWeb}
                                                onChange={handleChange}
                                                name="TriggerOnModifiedWeb"
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_trigger_on_modified_web'] || 'Trigger on WebUI modifications'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnModifiedProcessing}
                                                onChange={handleChange}
                                                name="TriggerOnModifiedProcessing"
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_trigger_on_modified_processing'] || 'Trigger on processing modifications'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnErasureProgress}
                                                onChange={handleChange}
                                                name="TriggerOnErasureProgress"
                                                color="primary"
                                            />
                                        }
                                        label={translations['external_trigger_on_erasure_progress'] || 'Trigger on erasure progress'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnSessionEnd}
                                                onChange={handleChange}
                                                name="TriggerOnSessionEnd"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_end_session'] || 'On end session'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnErasureFinished}
                                                onChange={handleChange}
                                                name="TriggerOnErasureFinished"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_erasure_finished'] || 'On erasure finished'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnAnalyzeFinished}
                                                onChange={handleChange}
                                                name="TriggerOnAnalyzeFinished"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_analyze_finished'] || 'On analyze finished'}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.TriggerOnOperatorWorkflowDone}
                                                onChange={handleChange}
                                                name="TriggerOnOperatorWorkflowDone"
                                                color="primary"
                                            />
                                        }
                                        label={translations['on_operator_workflow_done'] || 'On operator workflow done'}
                                    />
                                </FormGroup>
                                {mappingHelp ? <Tooltip title={translations["close_mapping_help"] || "Close mapping help screen"}>
                                    <IconButton onClick={handleMappingHelp}>

                                        <Close />
                                    </IconButton>
                                </Tooltip>
                                    :
                                    <Tooltip title={translations["need_help_mapping"] || "Need help with table mapping?"}>
                                        <IconButton onClick={handleMappingHelp}>

                                            <HelpOutline />
                                        </IconButton>
                                    </Tooltip>
                                }
                                {
                                    mappingHelp ?
                                        <JsonMappingHelp />
                                        : null
                                }
                                <TextField
                                    name="TableMapping"
                                    value={values.TableMapping}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    multiline={true}
                                    error={!!(errors.TableMapping && touched.TableMapping)}
                                    placeholder={translations["table_mapping"] || "Table mapping"}
                                    label={translations["table_mapping"] || "Table mapping"}
                                    margin={"dense"}
                                    fullWidth={true}
                                />
                            </>
                            <FormStep2 handleSubmit={handleSubmit} 
                                initialValues={initialValues} 
                                translations={translations} 
                                formSettings={formSettings} 
                                values={values} 
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                isSubmitting={isSubmitting}
                                isValid={isValid}
                                dirty={dirty}
                            />
                            {formSettings.ButtonSubmit ? (
                                <Button
                                    className={classes.submit}
                                    variant="contained"
                                    color={formSettings.ButtonSubmit.Color}
                                    type="submit"
                                    disabled={!edit ? !isValid || isSubmitting : !((isValid && dirty) || isDirty) || isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <CircularProgress size={24} />
                                    ) : (
                                            formSettings.ButtonSubmit.Title
                                        )}
                                </Button>
                            ) : null}
                        </form>
                    </div>
                );
            }}
        </Formik>
    );
};

export default withStyles(styles, { withTheme: true })(FormStep1Kloudless);